import { useState, useMemo } from 'react';
import { BookOpen, ChevronDown, ExternalLink } from 'lucide-react';

export interface CodeRef {
  term?: string;
  display?: string;
  code?: string;
  system?: string;
}

function _cleanString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

/** The one RxNav deep-link producer (rule 28 4a): the code-reference chips and
 * the DailyMed safety panel (#35) both build medication external links here,
 * so the target cannot drift between the two surfaces. */
export function rxNavSearchUrl(rxcui: string): string {
  return `https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=${_cleanString(rxcui)}`;
}

function _referenceTerm(ref: CodeRef): string {
  return _cleanString(ref.term) || _cleanString(ref.display) || _cleanString(ref.code) || 'Code reference';
}

function _referenceCode(ref: CodeRef): string {
  return _cleanString(ref.code);
}

function _referenceSystem(ref: CodeRef): string {
  const raw = _cleanString(ref.system);
  if (!raw) return 'ICD-10';
  if (raw.toUpperCase() === 'SNOMED') return 'SNOMED CT';
  return raw;
}

export function normalizeCodeReferences(value: unknown): CodeRef[] {
  if (!Array.isArray(value)) return [];
  const normalized: CodeRef[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const raw = item as Record<string, unknown>;
    const code = _cleanString(raw.code);
    const system = _referenceSystem(raw as CodeRef);
    const term = _cleanString(raw.term) || _cleanString(raw.display) || _cleanString(raw.label) || _cleanString(raw.name) || code;
    if (!code || !term) continue;
    normalized.push({ term, display: _cleanString(raw.display) || term, code, system });
  }
  return normalized;
}

// Non-clinical categorical tagging (which coding system, not severity) —
// --chart-categorical-* tokens, same index-per-system as clinicalMarkdownComponents.tsx.
const SYSTEM_STYLES: Record<string, {
  bg: string;
  accent: string;
  icon: string;
  label: string;
  link: (code: string) => string;
}> = {
  'ICD-10': {
    bg: 'bg-(--chart-categorical-1)/8 border-(--chart-categorical-1)/25',
    accent: 'text-(--chart-categorical-1)',
    icon: 'Dx',
    label: 'Diagnosis',
    link: (c) => `https://icdcodes.ai/icd10/${c}`,
  },
  RxNorm: {
    bg: 'bg-(--chart-categorical-2)/8 border-(--chart-categorical-2)/25',
    accent: 'text-(--chart-categorical-2)',
    icon: 'Rx',
    label: 'Medication',
    link: (c) => rxNavSearchUrl(c),
  },
  CPT: {
    bg: 'bg-(--chart-categorical-4)/8 border-(--chart-categorical-4)/25',
    accent: 'text-(--chart-categorical-4)',
    icon: 'Px',
    label: 'Procedure',
    link: (c) => `https://www.aapc.com/codes/cpt-codes/${c}`,
  },
  LOINC: {
    bg: 'bg-(--chart-categorical-3)/8 border-(--chart-categorical-3)/25',
    accent: 'text-(--chart-categorical-3)',
    icon: 'Lx',
    label: 'Lab/Test',
    link: (c) => `https://loinc.org/${c}`,
  },
  'SNOMED CT': {
    bg: 'bg-(--chart-categorical-6)/8 border-(--chart-categorical-6)/25',
    accent: 'text-(--chart-categorical-6)',
    icon: 'Sn',
    label: 'Clinical Finding',
    link: (c) => `https://termbrowser.nhs.uk/?perspective=full&conceptId1=${c}`,
  },
};

/** External browser URL for a resolved code (no API keys; safe for client). */
export function getCodeReferenceExternalUrl(system: string, code: string): string {
  const safeSystem = _referenceSystem({ system });
  const style = SYSTEM_STYLES[safeSystem] ?? SYSTEM_STYLES['ICD-10'];
  return style.link(_cleanString(code));
}

function CodeCard({ codeRef, index, scopeId }: { codeRef: CodeRef; index: number; scopeId: string }) {
  const safeSystem = _referenceSystem(codeRef);
  const safeCode = _referenceCode(codeRef);
  const safeTerm = _referenceTerm(codeRef);
  const style = SYSTEM_STYLES[safeSystem] ?? SYSTEM_STYLES['ICD-10'];
  const anchorId = scopeId ? `code-ref-${scopeId}-${index}` : `code-ref-${index}`;
  return (
    <a
      id={anchorId}
      href={style.link(safeCode)}
      target="_blank"
      rel="noopener noreferrer"
      className={`group flex items-center gap-3 rounded border px-3.5 py-2.5 transition-all duration-150
        hover:shadow-md hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${style.bg}`}
    >
      <div className={`relative flex items-center justify-center w-8 h-8 rounded-lg font-medium text-xs tracking-wide
        ${style.accent} bg-surface-overlay/60 ring-1 ring-inset ring-border/40`}>
        {style.icon}
        <span className={`absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full
          text-[8px] font-medium bg-surface-overlay ring-1 ring-inset ring-border/50 ${style.accent}`}>
          {index}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-foreground truncate leading-tight">
          {safeTerm}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`font-mono text-xs font-medium ${style.accent}`}>{safeCode}</span>
          <span className="text-[10px] text-text-tertiary font-medium">{safeSystem}</span>
        </div>
      </div>
      <ExternalLink className="w-3.5 h-3.5 text-text-tertiary/40 group-hover:text-text-tertiary transition-colors shrink-0" />
    </a>
  );
}

function _codeRefAnchorId(scopeId: string | undefined, idx: number): string {
  const safe = (scopeId && scopeId.trim()) || 'm';
  return `code-ref-${safe}-${idx}`;
}

/**
 * Inject in-text reference markers into markdown (standard links — works with react-markdown).
 * Matches clinical terms (from code_references) and appends a link to the Codes & References card.
 * Sorts by term length (longest first) so "Metformin 500 mg" matches before "Metformin".
 */
export function injectCodeMarkers(text: string, references: CodeRef[], scopeId?: string): string {
  if (!references || references.length === 0) return text;

  let result = text;
  const used = new Set<number>();
  const sorted = [...references].sort((a, b) => _referenceTerm(b).length - _referenceTerm(a).length);

  for (let i = 0; i < sorted.length; i++) {
    const ref = sorted[i];
    const idx = references.indexOf(ref) + 1;
    const escaped = _referenceTerm(ref).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const anchor = _codeRefAnchorId(scopeId, idx);
    const marker = ` [${idx}](#${anchor})`;

    // Try bold form first: **Term**
    const boldRe = new RegExp(`(\\*\\*${escaped}\\*\\*)(?!\\s*\\[)`, 'i');
    if (boldRe.test(result) && !used.has(idx)) {
      result = result.replace(boldRe, `$1${marker}`);
      used.add(idx);
      continue;
    }

    // Fall back to plain term (word boundary, first occurrence only)
    const plainRe = new RegExp(`(?<!\\*\\*)(\\b${escaped}\\b)(?!\\*\\*)(?!\\s*\\[)`, 'i');
    if (plainRe.test(result) && !used.has(idx)) {
      result = result.replace(plainRe, `**$1**${marker}`);
      used.add(idx);
    }
  }

  return result;
}

export function CodeReferencesPanel({ references, scopeId }: { references: CodeRef[]; scopeId?: string }) {
  const [isExpanded, setIsExpanded] = useState(true);

  const grouped = useMemo(() => {
    if (!references || references.length === 0) return {};
    return references.reduce<Record<string, { ref: CodeRef; idx: number }[]>>((acc, ref, i) => {
      const key = _referenceSystem(ref);
      if (!acc[key]) acc[key] = [];
      acc[key].push({ ref, idx: i + 1 });
      return acc;
    }, {});
  }, [references]);

  if (!references || references.length === 0) return null;

  const systemOrder = ['ICD-10', 'RxNorm', 'CPT', 'LOINC'];
  const sortedSystems = systemOrder.filter(s => grouped[s]);
  Object.keys(grouped).forEach(s => {
    if (!sortedSystems.includes(s)) sortedSystems.push(s);
  });

  return (
    <div className="rounded-2xl border border-border/50 bg-linear-to-br from-surface-sunken/20 via-background to-surface-sunken/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2.5 px-4 py-3.5 text-left hover:bg-surface-sunken/30 transition-colors"
      >
        <BookOpen className="w-4 h-4 text-primary shrink-0" />
        <span className="text-sm font-medium text-foreground flex-1">
          Codes & References
        </span>
        <span className="text-xs text-text-tertiary tabular-nums">{references.length} code{references.length !== 1 ? 's' : ''}</span>
        <ChevronDown className={`w-4 h-4 text-text-tertiary transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
          {sortedSystems.map(system => (
            <div key={system}>
              <div className="text-[10px] font-medium uppercase tracking-widest text-text-tertiary/60 mb-1.5 pl-0.5">
                {SYSTEM_STYLES[system]?.label ?? system}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {grouped[system].map(({ ref: item, idx }) => (
                  <CodeCard key={`${item.code}-${idx}`} codeRef={item} index={idx} scopeId={scopeId ?? ''} />
                ))}
              </div>
            </div>
          ))}
          <div className="text-[10px] text-text-tertiary/50 pt-1 text-right">
            Codes verified via UMLS Terminology Services
          </div>
        </div>
      )}
    </div>
  );
}
